import { type InvestmentAccountType, useWealthOverview } from '@egw/api'
import { Field, ModalDrawer, PageHeader } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { AccountNameForm } from '@egw/features-account'
import { useTranslation } from '@egw/i18n'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { type FC, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useParams } from 'react-router'

export const InvestmentsAccountDetail: FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation('features-investment')

  const { alias = '' } = useParams<{ alias: string }>()

  const [nameUpdating, setNameUpdating] = useState<boolean>(false)

  const { data, isLoading, refetch, isError } = useWealthOverview({ enabled: false })

  const investmentAccountDetail = data?.personalPortfolio?.accounts.find((account) => account.alias === parseInt(alias))

  // Companion Account Names cannot be edited
  const canEditAccountName =
    investmentAccountDetail?.__typename === 'InvestmentBrokerageAccount' ||
    investmentAccountDetail?.__typename === 'InvestmentRothIRAAccount' ||
    investmentAccountDetail?.__typename === 'InvestmentIRAAccount' ||
    investmentAccountDetail?.__typename === 'InvestmentInheritedIRAAccount' ||
    investmentAccountDetail?.__typename === 'InvestmentInheritedRothIRAAccount' ||
    investmentAccountDetail?.__typename === 'InvestmentJointBrokerageTenantsInCommonAccount' ||
    investmentAccountDetail?.__typename === 'InvestmentJointBrokerageTenantsWithRightOfSurvivorshipAccount'

  return (
    <>
      {isLoading ? (
        <div className="egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-xl" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {investmentAccountDetail ? (
        <>
          <PageHeader
            title={t('account_details.title')}
            subtitle={investmentAccountDetail.accountName}
            backLinkUrl={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_DETAIL.backPath?.replace(':alias', alias.toString())}
          />
          <div className="egw-container pt-0">
            <div className="flex flex-col gap-2xs mb-lg">
              <div className="border-b border-borderDark pb-xs">
                <Field
                  label={t('account_details.account_type')}
                  value={t(`account_type.${investmentAccountDetail.__typename}`)}
                />
              </div>
              {'accountTitle' in investmentAccountDetail && investmentAccountDetail?.accountTitle && (
                <div className="border-b border-borderDark pb-xs">
                  <Field
                    label={t('account_details.account_registration_title')}
                    value={investmentAccountDetail?.accountTitle}
                  />
                </div>
              )}
              <div className="border-b border-borderDark pb-xs">
                <Field
                  isPII
                  label={t('account_details.account_number')}
                  value={investmentAccountDetail.accountNumber}
                  canCopy
                />
              </div>
              <div className="pb-xs">
                {nameUpdating ? (
                  <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
                    <Skeleton count={1} height="50px" />
                  </SkeletonTheme>
                ) : (
                  <Field
                    label={t('account_details.account_name')}
                    value={investmentAccountDetail.accountName}
                    onEdit={
                      canEditAccountName
                        ? () => {
                            setIsOpen(true)
                          }
                        : undefined
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </>
      ) : null}

      {isError ? (
        <div className="egw-container">
          <InformationCircleIcon className="w-2xl h-2xl fill-red-500 mb-sm" />
          <p className="text-text-error mb-xl typo-primary-2xl-light">{t('common.generic_error')}</p>
          <p className="text-text-body typo-primary-xl">{t('activity.error')}</p>
        </div>
      ) : null}

      <ModalDrawer
        title={t('account_details.change_account_name')}
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false)
        }}
      >
        {investmentAccountDetail ? (
          <AccountNameForm
            accountType={investmentAccountDetail.__typename as InvestmentAccountType}
            alias={investmentAccountDetail.alias}
            accountName={investmentAccountDetail.accountName}
            onSuccess={async () => {
              setNameUpdating(true)
              setIsOpen(false)
              await refetch().then(() => {
                setNameUpdating(false)
              })
            }}
          />
        ) : null}
      </ModalDrawer>
    </>
  )
}
